import { Link } from "react-router-dom";
import { Instagram, Mail, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const LOGO_URL = "/assets/lotus-logo.png";

const Footer = ({ onOpenBooking }) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const handleScheduleCall = (e) => {
    e.preventDefault();
    if (onOpenBooking) onOpenBooking();
  };

  return (
    <footer data-testid="footer" className="footer py-16 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img
                src={LOGO_URL}
                alt="Ivana Silva Coaching"
                className="h-28 md:h-32 w-auto brightness-150"
                data-testid="footer-logo"
              />
              <div>
                <span className="font-heading text-2xl text-[#F9F8F6]">
                  Ivana Silva
                </span>
                <span className="block text-xs text-[#F9F8F6]/70 tracking-[0.15em] uppercase">
                  {t("nav.coaching")}
                </span>
              </div>
            </Link>
            <p className="text-[#F9F8F6]/80 font-light leading-relaxed max-w-sm">
              {t("footer.tagline")}
            </p>
            <div className="flex gap-4 mt-6">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 rounded-full bg-white/10 hover:bg-[#C5A059] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="mailto:coaching@ivanasilva.com"
                className="p-3 rounded-full bg-white/10 hover:bg-[#C5A059] transition-colors"
                data-testid="social-email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <h4 className="font-heading text-lg text-[#F9F8F6] mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-3">
              <li>
                <a href="/#programs" className="text-[#F9F8F6]/70 hover:text-[#C5A059]">
                  {t("footer.programs")}
                </a>
              </li>
              <li>
                <a href="/#about" className="text-[#F9F8F6]/70 hover:text-[#C5A059]">
                  {t("footer.about")}
                </a>
              </li>
              <li>
                <a href="/#testimonials" className="text-[#F9F8F6]/70 hover:text-[#C5A059]">
                  {t("footer.success")}
                </a>
              </li>
              <li>
                <a href="/#quiz" className="text-[#F9F8F6]/70 hover:text-[#C5A059]">
                  {t("footer.quiz")}
                </a>
              </li>
              <li>
                <Link
                  to="/decode-guide"
                  className="text-[#F9F8F6]/70 hover:text-[#C5A059]"
                  data-testid="footer-freebie-link"
                >
                  {t("footer.detox")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="font-heading text-lg text-[#F9F8F6] mb-4">{t("footer.getInTouch")}</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:coaching@ivanasilva.com"
                  className="flex items-center gap-3 text-[#F9F8F6]/70 hover:text-[#C5A059] transition-colors"
                  data-testid="footer-email-link"
                >
                  <Mail className="w-4 h-4 text-[#C5A059]" />
                  coaching@ivanasilva.com
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={handleScheduleCall}
                  className="flex items-center gap-3 text-[#F9F8F6]/70 hover:text-[#C5A059] transition-colors"
                  data-testid="footer-schedule-call"
                >
                  <Phone className="w-4 h-4 text-[#C5A059]" />
                  {t("footer.scheduleCall")}
                </button>
              </li>
            </ul>
            <button
              type="button"
              onClick={handleScheduleCall}
              className="mt-6 w-full text-left p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-colors group"
              data-testid="footer-inquiry-cta"
            >
              <p className="text-[#C5A059] text-sm font-medium mb-1 group-hover:text-[#DCC286]">{t("footer.readyTitle")}</p>
              <p className="text-[#F9F8F6]/70 text-sm">
                {t("footer.readySubtitle")}
              </p>
            </button>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#F9F8F6]/50 text-sm">
            {t("footer.rights", { year: currentYear })}
          </p>
          <div className="flex items-center gap-6 text-sm text-[#F9F8F6]/50">
            <a href="#" className="hover:text-[#C5A059]">{t("footer.privacy")}</a>
            <a href="#" className="hover:text-[#C5A059]">{t("footer.terms")}</a>
            <LanguageSwitcher variant="dark" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
